import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { tokenIsExpired } from '../../authUtils';
import useSessionManager from '../../hooks/useSessionManager';

/**
 * Protected Route component that ensures authentication before rendering.
 * Optionally accepts a `requiredPermission` string — if provided, the user
 * must have that claim (or be a SuperAdmin / Baiju_Ta) to access the route.
 * Automatically redirects to login if session is invalid.
 *
 * requiredPermission format: 'UserRead Master' | 'UserRead Transaction' | etc.
 */
const ProtectedRoute = ({ component: Component, requiredPermission, ...rest }) => {
    const { isAuthenticated, token, permissions, isSuperAdmin, username } = useAuth();
    const { isSessionValid } = useSessionManager();

    return (
        <Route
            {...rest}
            render={(props) => {
                // Check authentication status
                if (!isAuthenticated) {
                    return <Redirect to="/" />;
                }

                // Check token validity
                if (!token || tokenIsExpired(token)) {
                    return <Redirect to="/" />;
                }

                // Check session validity using session manager
                if (!isSessionValid) {
                    return <Redirect to="/" />;
                }

                // SuperAdmin or privileged username has full access
                const hasFullAccess = isSuperAdmin || username === 'Baiju_Ta';

                // Check permission claim — requires the specific claim (e.g. 'UserRead Master')
                if (requiredPermission && !hasFullAccess && !permissions.includes(requiredPermission)) {
                    return <Redirect to="/Home" />;
                }

                // All checks passed, render the component
                return <Component {...props} />;
            }}
        />
    );
};

export default ProtectedRoute;